import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

export default function Renewals({ renewalView = '' }) {
  const [renewals, setRenewals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/customers/expiring`, { headers: getHeaders() });
      setRenewals(res.data);
    } catch (err) { console.log(err); }
    finally { setLoading(false); }
  }, [getHeaders]);

  useEffect(() => { fetchRenewals(); }, [fetchRenewals]);

  useEffect(() => {
    const filterMap = {
      'renewals-all': 'all',
      'renewals-overdue': 'overdue',
      'renewals-today': 'today',
      'renewals-7days': '7days',
      'renewals-30days': '30days'
    };
    if (filterMap[renewalView]) setFilter(filterMap[renewalView]);
  }, [renewalView]);

  const filtered = renewals.filter(r => {
    if (filter === 'overdue') return r.days_left < 0;
    if (filter === 'today') return r.days_left === 0;
    if (filter === '7days') return r.days_left > 0 && r.days_left <= 7;
    if (filter === '30days') return r.days_left > 0 && r.days_left <= 30;
    return true;
  });

  const getDaysColor = (days) => {
    if (days < 0) return { bg: '#fee2e2', color: '#dc2626' };
    if (days === 0) return { bg: '#fef3c7', color: '#d97706' };
    if (days <= 7) return { bg: '#ffedd5', color: '#ea580c' };
    return { bg: '#d1fae5', color: '#065f46' };
  };

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#1e3a5f', margin: 0 }}>Renewals</h2>
        <button onClick={fetchRenewals} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>🔄 Refresh</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Overdue', value: renewals.filter(r => r.days_left < 0).length, color: '#dc2626', key: 'overdue' },
          { label: 'Expiring Today', value: renewals.filter(r => r.days_left === 0).length, color: '#d97706', key: 'today' },
          { label: 'Within 7 Days', value: renewals.filter(r => r.days_left > 0 && r.days_left <= 7).length, color: '#ea580c', key: '7days' },
          { label: 'Within 30 Days', value: renewals.filter(r => r.days_left > 0 && r.days_left <= 30).length, color: '#3b82f6', key: '30days' },
        ].map(card => (
          <div key={card.key} onClick={() => setFilter(card.key)} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderTop: `4px solid ${card.color}`, cursor: 'pointer' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[{ key: 'all', label: 'All' }, { key: 'overdue', label: 'Overdue' }, { key: 'today', label: 'Today' }, { key: '7days', label: '7 Days' }, { key: '30days', label: '30 Days' }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: '7px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', background: filter === f.key ? '#1e3a5f' : 'white', color: filter === f.key ? 'white' : '#374151', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
            {f.label}
          </button>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading...</div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1e3a5f', color: 'white' }}>
                {['#', 'Customer', 'Mobile', 'Vehicle', 'IMEI', 'Renewal Date', 'Days Left', 'Dealer', 'Payment'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>🎉 No renewals due in this category!</td></tr>
              ) : filtered.map((r, i) => {
                const dc = getDaysColor(r.days_left);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{r.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{r.mobile}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{r.vehicle_no || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{r.imei || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{formatDate(r.renewal_date)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: dc.bg, color: dc.color, padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                        {r.days_left < 0 ? `${Math.abs(r.days_left)} days overdue` : r.days_left === 0 ? 'Today!' : `${r.days_left} days`}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{r.dealer_name || 'Direct'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: r.payment_status === 'paid' ? '#d1fae5' : '#fee2e2', color: r.payment_status === 'paid' ? '#065f46' : '#dc2626' }}>{r.payment_status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}
